<template>
  <module-page v-bind="pageParams">
    <v-layout row wrap>
      <v-flex xs12>
        <v-tabs class="page__tabs" hide-slider>
          <v-tab
            v-for="(tab, i) in pageParams.tabs"
            :to="tab.value"
            :key="i"
            active-class="white--text financial--background"
          >
            <span
              :class="
                hasPayableLateSlips && tab.icon ? 'enterprise--color' : ''
              "
            >
              {{ !mobile ? tab.title : tab.titleMobile }}
              <i
                v-if="hasPayableLateSlips && tab.icon"
                :class="`${tab.icon} side-text--icon`"
              />
            </span>
          </v-tab>
        </v-tabs>
        <div v-if="!mobile" class="to-expire-payment-slip__description">
          <p>{{ descriptionFirstLine }}</p>
          <p>
            {{ descriptionSecondLine }}
            <router-link
              v-if="ticketCrmEnabled"
              :to="{ name: 'ticketForm' }"
              tag="a"
              class="payment-slip-link"
            >
              <u>{{ descriptionLink }}</u>
            </router-link>
          </p>
        </div>
        <div v-else class="to-expire-payment-slip__description">
          <p>
            {{ descriptionFirstLine }} {{ descriptionSecondLine }}
            <router-link
              v-if="ticketCrmEnabled"
              :to="{ name: 'ticketForm' }"
              tag="a"
              class="payment-slip-link"
            >
              <u>{{ descriptionLink }}</u>
            </router-link>
          </p>
        </div>
        <screen-loading v-if="isLoadingPaymentSlips"></screen-loading>
        <div v-else-if="!hasPaymentSlipError">
          <template v-if="debitBlock">
            <empty-state-card icon="alert">
              <div slot="message" class="error--color">
                <p class="waiting-state__title">
                  <span>
                    <b>{{ $t('pages.paymentSlip.operationNotAllowed') }}</b>
                  </span>
                </p>
                <p class="waiting-state__text">
                  <span>{{
                    $t('pages.paymentSlip.partialSlipWarningSubTitle')
                  }}</span>
                </p>
                <p class="waiting-state__text">
                  <router-link
                    v-if="ticketCrmEnabled"
                    class="error--color font-weight-bold"
                    :to="{ name: 'ticketForm' }"
                    tag="a"
                  >
                    <u>{{ $t('pages.paymentSlip.supportLink') }}</u>
                  </router-link>
                </p>
              </div>
            </empty-state-card>
          </template>
          <template v-else-if="!hasToExpireSlips">
            <empty-state-card :icon="enums.paymentSlipWaitingState.icon">
              <div slot="message" v-html="emptyPaymentSlipsHtml" />
            </empty-state-card>
          </template>
          <template v-else>
            <span v-for="(contract, i) in allowedContracts" :key="i">
              <!-- ToExpire Slips List -->
              <ToExpireTable
                :title="getContractTitle(contract)"
                :items="toExpireSlipsByContract[contract.id]"
                :unitFinancialPositions="toExpireSlipsByContract"
                :expand-column="expandColumn"
                :slip-details-headers="slipDetailsHeaders"
                :email="email"
                :cellphone="cellphone"
                :debitBlock="debitBlock"
              >
              </ToExpireTable>
            </span>
          </template>
        </div>
        <div v-else class="empty-state">{{ $t('responses.error') }}</div>
      </v-flex>
    </v-layout>
  </module-page>
</template>

<script>
import bus from '@/utils/bus';
import enums from '@/utils/enums';
import ModulePage from '@/components/layout/Page';
import ScreenLoading from '@/components/loaders/ScreenLoading';
import EmptyStateCard from '@/components/layout/EmptyStateCard';
import PaymentButton from '@/components/financial/PaymentButton';
import slipMixin from '@/mixins/slip';
import toExpirePaymentSlipMixin from '@/mixins/modules/toExpirePaymentSlip';
import {
  APP,
  CONFIG,
  USER,
  TICKET_CRM,
  PAYMENT_SLIPS,
  FINANCIAL_POSITION,
} from '../../utils/constants';
import ToExpireTable from '../../components/financial/ToExpireTable.vue';
export default {
  name: 'toExpirePaymentSlip',
  components: {
    ModulePage,
    ScreenLoading,
    EmptyStateCard,
    PaymentButton,
    ToExpireTable,
  },
  mixins: [slipMixin, toExpirePaymentSlipMixin],
  computed: {
    allowedContracts() {
      const { TO_EXPIRE_SLIPS } = enums.financialModules;
      return this.getAllowedContracts(
        TO_EXPIRE_SLIPS,
        this.selectedUnit.contracts
      );
    },
    imobmeUser() {
      return this.$store.getters[USER.GET_IMOBME_DATA];
    },
    ticketCrmEnabled() {
      return this.$store.getters[TICKET_CRM.GET_ENABLED];
    },
    emptyPaymentSlipsHtml() {
      return this.$t('pages.paymentSlip.emptyToExpireSlips', {
        extraInfo: this.hasFuturePaymentSlips
          ? this.$t('pages.paymentSlip.hasFutureToExpireSlips')
          : '',
      });
    },
    hasToExpireSlips() {
      const contracts = this.allowedContracts;
      return contracts.some(
        (c) => !!this.toExpireSlipsByContract[c.id]?.length
      );
    },
    slipDetailsHeaders() {
      return this.visibleHeaders(this.slipDetailsHeadersConfig);
    },
    toExpireDays() {
      return this.$store.getters[CONFIG.GET_TO_EXPIRE_PERIOD_DAYS];
    },
    toExpireSlipsByContract() {
      return this.$store.getters[PAYMENT_SLIPS.GET_TO_EXPIRE_BY_CONTRACT];
    },
    contractPositionData() {
      return this.$store.getters[FINANCIAL_POSITION.GET_DATA];
    },
  },
  data() {
    return {
      loaded: true,
      expandColumn: 'slipsUnion',
      pageParams: this.$store.getters[APP.PAGES].paymentSlip,
      descriptionFirstLine: '',
      descriptionSecondLine: '',
      descriptionLink: '',
      headers: [
        {
          text: this.$t('pages.paymentSlip.expirationDate'),
          value: 'expirationDate',
          align: 'center',
        },
        {
          text: this.$t('pages.paymentSlip.discountedValue'),
          value: 'discountedValue',
          align: 'center',
        },
        { text: ' ', value: 'parcelCode', align: 'center' },
      ],
      email: '',
      cellphone: {
        countryCode: '+55',
        areaCode: '11',
        number: '',
      },
      slipDetailsHeadersConfig: [
        {
          text: 'Contrato',
          value: 'type',
          align: 'center',
          showMobile: false,
        },
        {
          text: 'Descrição',
          value: 'contractTitle',
          formatter: (v, item) => this.getContractTitle(item),
          align: 'center',
        },
        {
          text: 'Total',
          value: 'partialValue',
          align: 'center',
          formatter: this.formatMoney,
        },
      ],
    };
  },
  created() {
    bus.$on('unit:change', this.getSlips);
    bus.$on('unit:update', this.getSlips);
    bus.$on('toExpireSlip:requestReady', this.update);
    this.email = this.user.email;
    if (this.imobmeUser && this.imobmeUser.cellphone) {
      const { number, areaCode, countryCode } = this.imobmeUser.cellphone;
      this.cellphone = { number, areaCode, countryCode: countryCode || '+55' };
    }
    this.descriptionFirstLine =
      this.pageParams.texts.toExpire.description.firstLine;
    this.descriptionSecondLine =
      this.pageParams.texts.toExpire.description.secondLine;
    this.descriptionLink = this.pageParams.texts.toExpire.description.link;
    this.getSlips();
  },
  beforeDestroy() {
    bus.$off('unit:change', this.getSlips);
    bus.$off('unit:update', this.getSlips);
    bus.$off('toExpireSlip:requestReady', this.update);
  },
  methods: {
    visibleHeaders(headers, mobile = this.mobile) {
      return headers.filter((header) =>
        mobile ? header.showMobile !== false : header.showDesktop !== false
      );
    },
    update() {
      this.loaded = false;
      this.$forceUpdate();
      this.$nextTick(function () {
        this.loaded = true;
      });
    },
    async getSlips(unitObj = null) {
      this.getToExpirePaymentSlips({ byContract: true });
    },
  },
};
</script>

<style lang="scss">
@import '../../assets/scss/breakpoint';
.waiting-state {
  &__title {
    margin: 0;
    font-weight: var(--font-weight);
  }
  &__text {
    margin: 0;
    font-size: 11px;
  }
}

.empty-state__text {
  a {
    color: var(--financial-color);
  }
}

.to-expire-payment-slip {
  &__description {
    font-size: 12px;
    padding-bottom: 10px;
    color: var(--label-color);
    a {
      color: var(--financial-color);
    }
    p {
      margin: 0;
    }
  }

  &__pay-btn {
    flex: 0.2;
    z-index: 1;
    height: 25px;
    min-width: 60px;
    font-size: 12px;
    margin: 0;
    padding: 5px 14px !important;
    border-radius: 5px;
    text-decoration: none;
    color: #fff;
    text-align: center;
    line-height: 25px;
  }
}

.payment-slip-link {
  cursor: pointer;
  font-weight: bold;
}
</style>



// WEBPACK FOOTER //
// src/views/financial/ToExpirePaymentSlip.vue